<script setup lang="ts">
import { BuilderContent } from '@builder.io/sdk-vue';
import { useElementVisibility } from '@vueuse/core';
import { useHead } from '@vueuse/head';
import {
  ComponentPublicInstance,
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  unref,
  watch,
} from 'vue';
import { RouteLocationNormalized, useRoute } from 'vue-router';

import CmsContent from '@/components/cms/CmsContent.vue';
import StaticHomePage from '@/components/homepage/StaticHomePage.vue';
import Theme404 from '@/components/homepage/Theme404.vue';
import { isEditingSSR, useCms } from '@/composables/useCms';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { useLayout } from '@/stores/layout';
import { trackFlagExperiment } from '@/utils/analytics/experimentViewedEvent';

interface CmsPage extends BuilderContent {
  data?: BuilderContent['data'] & {
    description?: string;
    excludeFooter: boolean;
    excludeHeader: boolean;
    keywords?: string[];
    previewImage?: string;
    searchable: boolean;
    title?: string;
    url?: string;
  };
}

const route = useRoute();
const layoutStore = useLayout();

const pageContent = ref<ComponentPublicInstance>();
const contentVisible = useElementVisibility(pageContent);
const { content, loadCmsContent } = useCms<CmsPage>('page', {
  contentVisible: () => contentVisible.value,
  prefetch: 'setup',
});

useHead(
  computed(() => {
    if (!content.value.result) return {};
    const contentData = unref(content.value.result.data);
    return {
      link: [{ rel: 'canonical', href: `https://nuts.com${contentData?.url ?? ''}` }],
      meta: [
        { name: 'description', content: contentData?.description },
        { name: 'keywords', content: contentData?.keywords?.join(',') },
        { name: 'robots', content: contentData?.searchable ? undefined : 'noindex,nofollow' },
        { property: 'og:description', content: contentData?.description, key: 'og:description' },
        { property: 'og:image', content: contentData?.previewImage, key: 'og:image' },
        { property: 'og:title', content: contentData?.title, key: 'og:title' },
        { property: 'og:url', content: `https://nuts.com${route.path}`, key: 'og:url' },
      ].filter((m) => !!m.content),
      title: contentData?.title?.concat(
        contentData.title.endsWith('Nuts.com') ? '' : ' - Nuts.com',
      ),
    };
  }),
);

onMounted(() => {
  watch(
    () => route.path,
    async () => {
      await loadCmsContent();
    },
  );

  watch(
    () => content.value.result,
    (result) => {
      layoutStore.displayFooter = !result?.data?.excludeFooter;
      layoutStore.displayHeader = !result?.data?.excludeHeader;
    },
    { immediate: true },
  );
});

onBeforeUnmount(() => {
  layoutStore.displayFooter = true;
  layoutStore.displayHeader = true;
});

defineOptions({
  beforeRouteEnter(to, _from, next) {
    const { getFlag } = useFeatureFlags();
    if (to.path === '/custom/snackbox') {
      getFlag('enableSubscriptionOnCSB')();
      trackFlagExperiment(useFeatureFlags().flagsMetadata.enableSubscriptionOnCSB);
    }
    next();
  },
  async criticalData(_: any, to: RouteLocationNormalized) {
    if (!import.meta.env.SSR) return;
    if (isEditingSSR(to.query)) return; // do not SSR for visual editor; currently crashes during hydration
    const page = useCms<CmsPage>('page', { prefetch: 'criticalData' });
    await page.loadCmsContent(to.path, to.query);
    const criticalLayoutStore = useLayout();
    criticalLayoutStore.displayFooter = !page.content.value.result?.data?.excludeFooter;
    criticalLayoutStore.displayHeader = !page.content.value.result?.data?.excludeHeader;
  },
});
</script>

<template>
  <div role="main">
    <CmsContent :content model="page" v-slot="slotProps" ref="pageContent">
      <StaticHomePage v-if="route.path === '/'" :fallbackReason="slotProps?.error" unexpected />
      <Theme404 v-else />
    </CmsContent>
  </div>
</template>
